import { useEffect, useState } from 'react';
import { TRACKS } from '../../constants/appConstants.js';
import { adminListLearners, adminLatestMoods, adminMoodHistory, adminLearnerStates, adminLessonTitleMap, adminLearnerProgress, adminDeleteLearner, adminSendPasswordReset } from '../../services/adminClient.js';
import { T, card, eyebrow, PageHeader, subscriptionBadge, Pill } from './ui.jsx';

const trackName = (id) => TRACKS[id]?.label || id || '—';
const MOOD_EMOJI = { stressed: '😣', tired: '😕', okay: '😐', good: '🙂', motivated: '🔥' };

const FILTERS = [
  { id: 'all',   label: 'All' },
  { id: 'free',  label: 'Free' },
  { id: 'paid',  label: 'Paid' },
  { id: 'admin', label: 'Admin' },
];

export default function AdminLearners() {
  const [rows, setRows] = useState(null);
  const [moods, setMoods] = useState({});
  const [states, setStates] = useState({});
  const [titles, setTitles] = useState({});
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let c = false;
    Promise.all([adminListLearners(), adminLatestMoods(), adminLearnerStates(), adminLessonTitleMap()])
      .then(([l, m, st, tm]) => {
        if (c) return;
        setRows(l || []); setMoods(m || {}); setTitles(tm || {});
        const sm = {}; (st || []).forEach(s => { sm[s.learner_id] = s; }); setStates(sm);
      })
      .catch(() => { if (!c) setRows([]); });
    return () => { c = true; };
  }, []);

  if (!rows) return <p style={{ color: T.slate, fontSize: 13 }}>Loading learners…</p>;

  const filtered = rows.filter(l => {
    if (q && !(l.email || '').toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === 'paid'  && l.subscription_status !== 'active') return false;
    if (filter === 'free'  && l.subscription_status === 'active') return false;
    if (filter === 'admin' && !l.is_admin) return false;
    return true;
  });

  const th = { textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: T.slate, padding: '0 10px 10px' };
  const td = { fontSize: 13, color: T.ink, padding: '11px 10px', borderTop: `1px solid ${T.border}` };

  return (
    <div>
      <PageHeader title="Learners" subtitle={`${rows.length} total · ${rows.filter(l => l.onboarding_completed_at).length} personalized (reached Roadmap onboarding)`} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by email…"
          style={{ flex: '1 1 240px', padding: '9px 13px', borderRadius: 9, border: `1px solid ${T.border}`, background: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {FILTERS.map(f => {
            const on = filter === f.id;
            return <button key={f.id} type="button" onClick={() => setFilter(f.id)}
              style={{ padding: '8px 14px', borderRadius: 99, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${on ? T.amber : T.border}`, background: on ? '#fffaf0' : '#fff', color: on ? T.amber : T.slate }}>{f.label}</button>;
          })}
        </div>
      </div>

      <div style={{ ...card, padding: '16px 18px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead><tr>
            <th style={th}>Email</th><th style={th}>Track</th><th style={th}>Current Lesson</th><th style={th}>Onboarded</th><th style={th}>Subscription</th><th style={th}>Last Active</th><th style={th}>Mood</th>
          </tr></thead>
          <tbody>
            {filtered.map(l => {
              const mood = moods[l.id]?.mood;
              return (
                <tr key={l.id} onClick={() => setDetail(l)} style={{ cursor: 'pointer' }}>
                  <td style={td}>
                    <span style={{ fontWeight: 600 }}>{l.email}</span>
                    {l.is_admin && <span style={{ marginLeft: 8 }}><Pill text="Admin" color={T.purple} bg="rgba(124,58,237,0.12)" /></span>}
                  </td>
                  <td style={{ ...td, color: T.slate }}>{trackName(l.active_track_id || l.current_track)}</td>
                  <td style={{ ...td, color: T.slate, fontSize: 12 }}>{titles[states[l.id]?.current_lesson_id] || '—'}</td>
                  <td style={td}>{l.onboarding_completed_at
                    ? <span title={`Personalized ${new Date(l.onboarding_completed_at).toLocaleString()}`}><Pill text="Yes" color={T.emerald} bg="rgba(5,150,105,0.12)" /></span>
                    : <span style={{ fontSize: 12, color: T.slate }}>—</span>}</td>
                  <td style={td}>{subscriptionBadge(l.subscription_status, l.tier)}</td>
                  <td style={{ ...td, color: T.slate, fontSize: 12 }}>{states[l.id]?.updated_at ? new Date(states[l.id].updated_at).toLocaleDateString() : '—'}</td>
                  <td style={td}>{mood ? <span title={mood}>{MOOD_EMOJI[mood] || '•'} <span style={{ fontSize: 11, color: T.slate }}>{mood}</span></span> : <span style={{ color: T.slate, fontSize: 12 }}>—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p style={{ fontSize: 13, color: T.slate, margin: '14px 0 0', textAlign: 'center' }}>No learners match.</p>}
      </div>

      {detail && (
        <LearnerDetail
          learner={detail}
          onClose={() => setDetail(null)}
          onDeleted={(id) => { setRows(rs => (rs || []).filter(r => r.id !== id)); setDetail(null); }}
        />
      )}
    </div>
  );
}

function LearnerDetail({ learner, onClose, onDeleted }) {
  const [history, setHistory] = useState(null);
  const [prog, setProg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);          // { kind: 'ok'|'err', text }
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    let c = false;
    adminMoodHistory(learner.id, 7).then(h => { if (!c) setHistory(h || []); });
    adminLearnerProgress(learner.id).then(p => { if (!c) setProg(p || []); });
    return () => { c = true; };
  }, [learner.id]);

  const phoneDigits = (learner.phone || '').replace(/[^\d+]/g, '');

  const handleReset = async () => {
    setBusy(true); setNote(null);
    const r = await adminSendPasswordReset(learner.email);
    setBusy(false);
    setNote(r.ok ? { kind: 'ok', text: `Password-reset email sent to ${learner.email}.` }
                 : { kind: 'err', text: r.error });
  };

  const handleDelete = async () => {
    setBusy(true); setNote(null);
    const r = await adminDeleteLearner(learner.id);
    setBusy(false);
    if (r.ok) { onDeleted?.(learner.id); return; }
    setConfirmDelete(false);
    setNote({ kind: 'err', text: r.error });
  };

  const row = (k, v) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}`, gap: 12 }}>
      <span style={{ fontSize: 12.5, color: T.slate }}>{k}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, textAlign: 'right', wordBreak: 'break-all' }}>{v ?? '—'}</span>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: '24px 26px', position: 'relative', boxShadow: '0 12px 48px rgba(0,0,0,0.25)' }}>
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 14, right: 14, width: 30, height: 30, borderRadius: '50%', border: `1px solid ${T.border}`, background: '#fff', color: T.slate, fontSize: 16, cursor: 'pointer', padding: 0 }}>×</button>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: T.ink, margin: '0 0 4px', paddingRight: 30 }}>{learner.email}</h2>
        <p style={{ fontSize: 12, color: T.slate, margin: '0 0 16px' }}>{trackName(learner.active_track_id || learner.current_track)} · joined {learner.created_at ? new Date(learner.created_at).toLocaleDateString() : '—'}</p>

        <p style={{ ...eyebrow, margin: '0 0 8px' }}>Subscription</p>
        {row('Status', learner.subscription_status)}
        {row('Tier', learner.tier)}
        {row('Stripe customer', learner.stripe_customer_id)}
        {row('Price id', learner.price_id)}

        <p style={{ ...eyebrow, margin: '16px 0 8px' }}>Profile</p>
        {row('Career goal', learner.career_goal)}
        {row('Onboarding (personalized)', learner.onboarding_completed_at ? new Date(learner.onboarding_completed_at).toLocaleString() : 'Not yet')}
        {row('Admin', learner.is_admin ? 'yes' : 'no')}

        <p style={{ ...eyebrow, margin: '16px 0 8px' }}>Mood history (last 7)</p>
        {history === null ? <p style={{ fontSize: 12, color: T.slate }}>Loading…</p> :
          history.length === 0 ? <p style={{ fontSize: 12, color: T.slate }}>No mood check-ins.</p> :
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                <span style={{ color: T.ink, fontWeight: 600 }}>{MOOD_EMOJI[m.mood] || '•'} {m.mood}</span>
                <span style={{ color: T.slate }}>{new Date(m.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>}

        <p style={{ ...eyebrow, margin: '16px 0 8px' }}>Lesson progress & mastery</p>
        {prog === null ? <p style={{ fontSize: 12, color: T.slate }}>Loading…</p> :
          prog.length === 0 ? <p style={{ fontSize: 12, color: T.slate }}>No lessons touched yet.</p> :
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {prog.map((p, i) => {
              const done = p.status === 'completed';
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}>
                  <span style={{ color: T.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.lessons?.title || 'Lesson'}</span>
                  <span style={{ flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
                    {p.mastery_score != null && <span style={{ color: T.slate }}>{Math.round(p.mastery_score * 100)}%</span>}
                    <span style={{ fontWeight: 700, color: done ? T.emerald : (p.mastery_level === 'struggling' ? T.coral : T.amber) }}>{done ? 'Completed' : (p.status || p.mastery_level || 'In progress')}</span>
                  </span>
                </div>
              );
            })}
          </div>}

        {/* Actions */}
        <p style={{ ...eyebrow, margin: '18px 0 8px' }}>Actions</p>
        {note && (
          <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 10px', color: note.kind === 'ok' ? T.emerald : T.coral }}>{note.text}</p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <a href={`mailto:${learner.email}`} style={actionBtn(T.slate)}>Email</a>
          {phoneDigits
            ? <a href={`sms:${phoneDigits}`} style={actionBtn(T.slate)}>Text</a>
            : <span style={{ ...actionBtn(T.slate), opacity: 0.45, cursor: 'not-allowed' }} title="No phone number on file">Text</span>}
          <button type="button" onClick={handleReset} disabled={busy} style={actionBtn(T.amber, true)}>Reset password</button>
          {learner.is_admin
            ? <span style={{ ...actionBtn(T.coral, true), opacity: 0.45, cursor: 'not-allowed' }} title="Admin accounts can't be deleted here">Delete</span>
            : <button type="button" onClick={() => setConfirmDelete(true)} disabled={busy} style={actionBtn(T.coral, true)}>Delete</button>}
        </div>

        {confirmDelete && (
          <div style={{ marginTop: 12, padding: '12px 14px', border: `1px solid ${T.coral}`, borderRadius: 10, background: 'rgba(220,38,38,0.05)' }}>
            <p style={{ fontSize: 12.5, color: T.ink, margin: '0 0 10px', lineHeight: 1.5 }}>
              Permanently delete <strong>{learner.email}</strong>? This removes their account, lesson progress, events, and all related data. <strong>This cannot be undone.</strong>
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={handleDelete} disabled={busy} style={actionBtn(T.coral, true)}>{busy ? 'Deleting…' : 'Yes, delete permanently'}</button>
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={busy} style={actionBtn(T.slate, true)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function actionBtn(color) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '8px 14px', borderRadius: 9, fontSize: 12.5, fontWeight: 700,
    fontFamily: 'inherit', textDecoration: 'none', cursor: 'pointer',
    border: `1px solid ${color}`, background: '#fff', color,
  };
}
